/**
 * Strukturiertes Briefing je Kooperation und je Beitrag (0073): Kernbotschaften, Do/Don't, Pflicht-Hashtags und
 * Markierungen, Kennzeichnung, Deadline, Hinweise. Ein Beitrag legt fest, was er selbst hat; der Rest kommt von der
 * Kooperation; ohne jedes Briefing gilt der Freitext der Kampagne. Dieselben Regeln stehen in SQL (app.briefing_list,
 * app.briefing_effective), damit Portal, Creator-Portal und Freigabe-Link dasselbe zeigen.
 * Reine Funktionen, getestet in packages/domain/test/briefing.test.ts.
 */
import type { Language } from "./types.js";

export interface BriefingFields {
  key_messages: string[];
  dos: string[];
  donts: string[];
  hashtags: string[];
  mentions: string[];
  ad_label: string | null;
  paid_partnership: boolean;
  deadline: string | null;
  notes: string | null;
}

/** Zeile aus deal_briefings (Team und Creator lesen sie). */
export interface BriefingRow extends BriefingFields {
  id: string;
  deal_id: string;
  deliverable_id: string | null;
  updated_at?: string;
}

/** Wirksames Briefing (public.briefing_view): strukturiert, Rückfall auf den Freitext der Kampagne oder nichts. */
export type EffectiveBriefing =
  | ({ source: "structured"; fallback_text: null } & BriefingFields)
  | { source: "fallback"; fallback_text: string }
  | { source: "none"; fallback_text: null };

export const BRIEFING_LIST_MAX = 20;
export const BRIEFING_ENTRY_MAX = 300;

export type BriefingListKind = "text" | "hashtag" | "mention";

/** Ein Eintrag gesäubert: Leerraum zusammengefasst; Hashtag ohne „#“, Markierung ohne „@“, jeweils ohne Leerzeichen. */
export function cleanBriefingEntry(v: string, kind: BriefingListKind): string {
  let s = v.replace(/\s+/g, " ").trim();
  if (kind === "hashtag") s = s.replace(/^#+/, "").replace(/ /g, "");
  if (kind === "mention") s = s.replace(/^@+/, "").replace(/ /g, "");
  return s;
}

export interface BriefingListResult {
  values: string[];
  error: string | null;
}

/** Liste säubern und prüfen wie app.briefing_list: leere und doppelte Einträge (ohne Groß/klein) fallen weg. */
export function cleanBriefingList(list: readonly string[], kind: BriefingListKind): BriefingListResult {
  const out: string[] = [];
  for (const raw of list) {
    const v = cleanBriefingEntry(raw ?? "", kind);
    if (!v || out.some((x) => x.toLowerCase() === v.toLowerCase())) continue;
    if (v.length > BRIEFING_ENTRY_MAX) return { values: out, error: `Ein Eintrag ist länger als ${BRIEFING_ENTRY_MAX} Zeichen.` };
    if (kind === "hashtag" && (v.length > 100 || /[#@,;]/.test(v))) return { values: out, error: `Ungültiger Hashtag: ${v}` };
    if (kind === "mention" && !/^[A-Za-z0-9._]{1,60}$/.test(v)) return { values: out, error: `Ungültige Markierung: @${v}` };
    out.push(v);
  }
  if (out.length > BRIEFING_LIST_MAX) return { values: out, error: `Höchstens ${BRIEFING_LIST_MAX} Einträge je Liste.` };
  return { values: out, error: null };
}

/** Eingabe aus einem Textfeld: eine Zeile je Eintrag; Hashtags und Markierungen dürfen auch mit Leerzeichen oder Komma getrennt sein. */
export function briefingLines(text: string, kind: BriefingListKind): string[] {
  const parts = kind === "text" ? text.split(/\r?\n/) : text.split(/[\s,;]+/);
  return parts.map((p) => p.trim()).filter(Boolean);
}

export function emptyBriefing(): BriefingFields {
  return { key_messages: [], dos: [], donts: [], hashtags: [], mentions: [], ad_label: null, paid_partnership: false, deadline: null, notes: null };
}

export function briefingIsEmpty(b: BriefingFields): boolean {
  return !b.key_messages.length && !b.dos.length && !b.donts.length && !b.hashtags.length && !b.mentions.length && !b.ad_label && !b.paid_partnership
    && !b.deadline && !b.notes;
}

/** Wie app.briefing_effective: Listen des Beitrags, wenn nicht leer, sonst der Kooperation; Texte zuerst vom Beitrag. */
export function effectiveBriefing(deal: BriefingFields | null | undefined, item: BriefingFields | null | undefined, fallbackText: string | null | undefined): EffectiveBriefing {
  if (!deal && !item) {
    const t = fallbackText?.trim();
    return t ? { source: "fallback", fallback_text: t } : { source: "none", fallback_text: null };
  }
  const list = (k: "key_messages" | "dos" | "donts" | "hashtags" | "mentions") => (item && item[k].length ? item[k] : deal?.[k] ?? []);
  return {
    source: "structured", fallback_text: null,
    key_messages: list("key_messages"), dos: list("dos"), donts: list("donts"), hashtags: list("hashtags"), mentions: list("mentions"),
    ad_label: item?.ad_label ?? deal?.ad_label ?? null,
    paid_partnership: !!item?.paid_partnership || !!deal?.paid_partnership,
    deadline: item?.deadline ?? deal?.deadline ?? null,
    notes: item?.notes ?? deal?.notes ?? null,
  };
}

/** Antwort von public.briefing_view bzw. dem Feld „briefing“ im Freigabe-Link in den Typ bringen (unbekannt → „none“). */
export function parseEffectiveBriefing(raw: unknown): EffectiveBriefing {
  const o = (typeof raw === "string" ? safeJson(raw) : raw) as Record<string, unknown> | null;
  if (!o || typeof o !== "object") return { source: "none", fallback_text: null };
  if (o.source === "fallback" && typeof o.fallback_text === "string") return { source: "fallback", fallback_text: o.fallback_text };
  if (o.source !== "structured") return { source: "none", fallback_text: null };
  const arr = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
  const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : null);
  return {
    source: "structured", fallback_text: null,
    key_messages: arr(o.key_messages), dos: arr(o.dos), donts: arr(o.donts), hashtags: arr(o.hashtags), mentions: arr(o.mentions),
    ad_label: str(o.ad_label), paid_partnership: o.paid_partnership === true, deadline: str(o.deadline)?.slice(0, 10) ?? null, notes: str(o.notes),
  };
}
function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

export const BRIEFING_LABELS: Readonly<Record<Language, Record<"title" | "keyMessages" | "dos" | "donts" | "hashtags" | "mentions" | "adLabel" | "paid" | "deadline" | "notes" | "fallback" | "none", string>>> = {
  de: {
    title: "Briefing", keyMessages: "Kernbotschaften", dos: "Do", donts: "Don't", hashtags: "Pflicht-Hashtags", mentions: "Pflicht-Markierungen",
    adLabel: "Kennzeichnung", paid: "Plattform-Kennzeichnung „Bezahlte Partnerschaft“ nutzen", deadline: "Deadline laut Briefing", notes: "Hinweise",
    fallback: "Briefing der Kampagne", none: "Kein Briefing hinterlegt.",
  },
  en: {
    title: "Briefing", keyMessages: "Key messages", dos: "Do", donts: "Don't", hashtags: "Required hashtags", mentions: "Required tags",
    adLabel: "Ad disclosure", paid: "Use the platform's “Paid partnership” label", deadline: "Briefing deadline", notes: "Notes",
    fallback: "Campaign briefing", none: "No briefing yet.",
  },
};

/** Hashtags und Markierungen zum Kopieren in die Caption: „#a #b @marke“. */
export function briefingCaptionTags(b: Pick<BriefingFields, "hashtags" | "mentions">): string {
  return [...b.hashtags.map((h) => `#${h}`), ...b.mentions.map((m) => `@${m}`)].join(" ");
}
